/**
 * Issue-reporting wire types — mirror the .NET DTOs in
 * AryanOnM.Application/DTOs/IssueReports + DTOs/Admin/UsageAnalyticsDto.
 *
 * The backend serializes enums as their string member NAMES (Program.cs registers
 * JsonStringEnumConverter), and properties are camelCase (JsonNamingPolicy.CamelCase),
 * so these string-literal const objects match the wire exactly. Mirrors the
 * WorkOrderStatus const-object pattern in types/work-orders.ts.
 */

export const IssueSeverity = {
  Low: 'Low',
  Medium: 'Medium',
  High: 'High',
  Critical: 'Critical',
} as const;
export type IssueSeverity = (typeof IssueSeverity)[keyof typeof IssueSeverity];

export const IssueCategory = {
  Bug: 'Bug',
  UiVisual: 'UiVisual',
  Performance: 'Performance',
  DataIncorrect: 'DataIncorrect',
  FeatureRequest: 'FeatureRequest',
  Other: 'Other',
} as const;
export type IssueCategory = (typeof IssueCategory)[keyof typeof IssueCategory];

export const IssueStatus = {
  New: 'New',
  Triaged: 'Triaged',
  InProgress: 'InProgress',
  Resolved: 'Resolved',
  Closed: 'Closed',
  WontFix: 'WontFix',
} as const;
export type IssueStatus = (typeof IssueStatus)[keyof typeof IssueStatus];

/** Open (non-terminal) statuses — used for the "open" filter + analytics. */
export const OPEN_ISSUE_STATUSES: readonly IssueStatus[] = [
  IssueStatus.New,
  IssueStatus.Triaged,
  IssueStatus.InProgress,
];

/** A captured console error / window error event (scrubbed). */
export interface CapturedConsoleError {
  readonly at: string; // ISO timestamp
  readonly level: 'error' | 'warn';
  readonly message: string;
  readonly source?: string;
}

/** A captured FAILED network/API call (scrubbed). */
export interface CapturedNetworkError {
  readonly at: string; // ISO timestamp
  readonly method: string;
  readonly url: string;
  readonly status: number; // 0 = network/timeout failure
  readonly statusText?: string;
  readonly durationMs?: number;
}

/** Auto-captured client context bundled into a new report. */
export interface ClientContext {
  readonly pageUrl: string;
  readonly routePath: string;
  readonly userAgent: string;
  readonly browser: string;
  readonly operatingSystem: string;
  readonly viewportWidth: number;
  readonly viewportHeight: number;
  readonly clientReportedAtUtc: string;
}

/** Scalar fields for a new report (images handled separately as File[]). */
export interface CreateIssueReportInput {
  readonly description: string;
  readonly severity: IssueSeverity;
  readonly category: IssueCategory;
  readonly context: ClientContext;
  readonly consoleErrors: readonly CapturedConsoleError[];
  readonly networkErrors: readonly CapturedNetworkError[];
}

export interface IssueAttachmentDto {
  readonly id: number;
  readonly fileName: string;
  readonly contentType: string;
  readonly sizeBytes: number;
  readonly uploadedAtUtc: string;
}

export interface IssueReportListItemDto {
  readonly id: number;
  readonly description: string;
  readonly severity: IssueSeverity;
  readonly category: IssueCategory;
  readonly status: IssueStatus;
  readonly reportedByUserId: number;
  readonly reportedByName: string | null;
  readonly reportedByEmail: string | null;
  readonly routePath: string | null;
  readonly attachmentCount: number;
  readonly firstAttachmentId: number | null;
  readonly createdAt: string;
}

export interface IssueReportDetailDto {
  readonly id: number;
  readonly description: string;
  readonly severity: IssueSeverity;
  readonly category: IssueCategory;
  readonly status: IssueStatus;
  readonly reportedByUserId: number;
  readonly reportedByName: string | null;
  readonly reportedByEmail: string | null;
  readonly pageUrl: string | null;
  readonly routePath: string | null;
  readonly userAgent: string | null;
  readonly browser: string | null;
  readonly operatingSystem: string | null;
  readonly viewportWidth: number | null;
  readonly viewportHeight: number | null;
  readonly consoleErrorsJson: string | null;
  readonly networkErrorsJson: string | null;
  readonly clientReportedAtUtc: string | null;
  readonly adminNotes: string | null;
  readonly resolvedByUserId: number | null;
  readonly resolvedAtUtc: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly attachments: readonly IssueAttachmentDto[];
}

export interface IssueReportListQuery {
  readonly q?: string;
  readonly status?: IssueStatus;
  readonly severity?: IssueSeverity;
  readonly category?: IssueCategory;
  readonly page?: number;
  readonly pageSize?: number;
}

export interface UpdateIssueStatusBody {
  readonly status: IssueStatus;
  readonly adminNotes?: string;
}

// ── Usage analytics (mirrors UsageAnalyticsDto) ──────────────────────────────

export interface UserCountsDto {
  readonly total: number;
  readonly active: number;
  readonly inactive: number;
}

export interface RecentLoginDto {
  readonly userId: number | null;
  readonly username: string;
  readonly loginTime: string;
  readonly isSuccessful: boolean;
  readonly failureReason: string | null;
}

export interface LoginActivityDto {
  readonly successfulLast24h: number;
  readonly failedLast24h: number;
  readonly successfulLast7d: number;
  readonly successfulLast30d: number;
  readonly distinctUsersLast7d: number;
  readonly distinctUsersLast30d: number;
  readonly recentLogins: readonly RecentLoginDto[];
}

export interface IssueStatsDto {
  readonly total: number;
  readonly open: number;
  readonly byStatus: Readonly<Record<string, number>>;
  readonly bySeverity: Readonly<Record<string, number>>;
}

export interface UsageAnalyticsDto {
  readonly generatedAtUtc: string;
  readonly users: UserCountsDto;
  readonly logins: LoginActivityDto;
  readonly entityRowCounts: Readonly<Record<string, number>>;
  readonly recentActivityUtc: Readonly<Record<string, string | null>>;
  readonly issues: IssueStatsDto;
}
